<!--
  Note: While PrimeVue suggests using the Select component instead of AutoComplete since v4,
  this component is still maintained for use cases that specifically require autocomplete functionality
  with free text input and suggestions, rather than selection from a fixed set of options.
-->
<template>
  <div class="p-field" :class="{ 'p-float-label': labelStyle === 'float' }">
    <div :class="{ 'p-field-horizontal': labelStyle === 'inline' }">
      <!-- Label -->
      <!-- <label v-if="props.label" :for="props.id" class="label-text">
        {{ props.label }}
        <span v-if="props.required" class="text-red-500">*</span>
      </label> -->
      <label v-if="props.label" :for="id" class="label-text flex justify-between" :class="{ 'p-error': isInvalid }">
        <div class="flex">
          <p>{{ props.label }}</p>
          <span v-if="props.required" class="text-red-500">*</span>
        </div>


        <slot name="adddata"></slot>
      </label>

      <!-- Input Wrapper -->
      <div class="p-input-wrapper">
        <div class="p-input-container relative">
          <AutoComplete ref="autoCompleteRef" :id="props.id" v-model="localValue" :suggestions="props.suggestions"
            :field="props.field" :option-label="props.optionLabel" :option-value="props.optionValue"
            :option-disabled="props.optionDisabled" :option-group-label="props.optionGroupLabel"
            :option-group-children="props.optionGroupChildren" :size="size" :class="[
              'w-full',
              {
                'p-filled': isFilled,
                'p-inputtext-lg': props.size === 'large',
                'p-invalid': isInvalid,
                'p-disabled': props.disabled,
                [`p-variant-${props.variant}`]: props.variant,
                'direction-aware': true
              }
            ]" :disabled="props.disabled" :placeholder="props.placeholder" :maxlength="props.maxLength"
            :minlength="props.minLength" :pattern="props.pattern" :aria-label="props.ariaLabel"
            :aria-labelledby="props.ariaLabelledby" :aria-invalid="isInvalid" :aria-required="props.required"
            :aria-describedby="ariaDescribedBy" :required="props.required" :readonly="props.readonly"
            :dropdown="dropdown" :dropdown-mode="props.dropdownMode" :auto-highlight="props.autoHighlight"
            :multiple="props.multiple" :force-selection="props.forceSelection" :min-input-length="props.minInputLength"
            :delay="props.delay" :complete-on-focus="props.completeOnFocus" :scroll-height="props.scrollHeight"
            :virtual-scroller-options="props.virtualScrollerOptions" :show-clear="props.showClear"
            :loading="props.loading" :loading-icon="props.loadingIcon" :remove-token-icon="props.removeTokenIcon"
            :dropdown-icon="props.dropdownIcon" :auto-selection="props.autoSelection" :autoOptionFocus="true"
            :panel-style="props.panelStyle" :panel-class="props.panelClass" :data-key="props.dataKey"
            :show-empty-message="props.showEmptyMessage" :empty-message="props.emptyMessage"
            :show-no-results="props.showNoResults" :no-results-message="props.noResultsMessage"
            :selection-mode="props.selectionMode" :item-size="props.itemSize" v-bind="$attrs" @complete="onComplete"
            @item-select="onItemSelect" @item-unselect="onItemUnselect"
            @dropdown-click="$emit('dropdown-click', $event)" @input="onInput" @focus="onFocus" @blur="onBlur"
            @keydown="onKeydown" @change="onChange" @show="$emit('show')" @hide="$emit('hide')" @clear="onClear">
            <!-- Header Template -->
            <template v-if="$slots.header" #header>
              <slot name="header"></slot>
            </template>

            <!-- Footer Template -->
            <template v-if="$slots.footer" #footer>
              <slot name="footer"></slot>
            </template>

            <!-- Empty Template -->
            <template v-if="$slots.empty" #empty>
              <slot name="empty"></slot>
            </template>

            <!-- Option Template -->
            <template #option="slotProps">
              <span v-html="highlightText(getOptionLabel(slotProps.option))"></span>
            </template>

            <!-- Option Group Template -->
            <template v-if="$slots.optiongroup" #optiongroup="slotProps">
              <slot name="optiongroup" v-bind="slotProps"></slot>
            </template>

            <!-- Selected Item Template -->
            <template v-if="$slots.chip" #chip="slotProps">
              <slot name="chip" v-bind="slotProps"></slot>
            </template>

            <!-- Remove Token Icon Template -->
            <template v-if="$slots.removetokenicon" #removetokenicon="slotProps">
              <slot name="removetokenicon" v-bind="slotProps"></slot>
            </template>

            <!-- Loading Icon Template -->
            <template v-if="$slots.loadingicon" #loadingicon>
              <slot name="loadingicon"></slot>
            </template>

            <!-- Dropdown Icon Template -->
            <!-- <template v-if="$slots.dropdownicon" #dropdownicon>
              <slot name="dropdownicon"></slot>
            </template> -->
          </AutoComplete>

          <!-- Clear Icon -->
          <i v-if="props.clearable && isFilled" 
             class="pi pi-times p-autocomplete-clear-icon absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500 hover:text-gray-700 bg-white rounded-full p-1 z-10" 
             @click="onClear"></i>
        </div>
      </div>
    </div>

    <!-- Error Messages -->
    <!-- Error Messages -->
    <div v-if="showErrorMessages" class="p-error-messages">
      <small v-if="props.helperText && !isInvalid" :id="helperTextId" class="p-hint">{{ props.helperText }}</small>
      <small v-if="shouldShowError" :id="`${props.id}-error`" class="p-error block text-red-500">
        {{ getFormattedErrorMessage }}
      </small>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { MOPAutocompleteProps } from './types'

export default defineComponent({
  name: 'MOPAutocomplete',
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import AutoComplete from 'primevue/autocomplete'
import type { AutoCompleteCompleteEvent, AutoCompleteChangeEvent } from 'primevue/autocomplete'
import { useFormContext } from '../../../../composables/useFormContext'
import { useLocalization } from '../../../../composables/useLocalization'
import { useVeeValidate } from '../../../../composables/useVeeValidate'

const props = withDefaults(defineProps<MOPAutocompleteProps>(), {
  modelValue: null,
  suggestions: () => [],
  field: undefined,
  optionLabel: undefined,
  optionValue: undefined,
  optionDisabled: undefined,
  optionGroupLabel: undefined,
  optionGroupChildren: undefined,
  size: undefined,
  invalid: false,
  disabled: false,
  readonly: false,
  required: false,
  placeholder: '',
  helperText: '',
  label: '',
  labelStyle: 'fixed',
  ariaLabel: '',
  ariaLabelledby: '',
  id: () => `mop-autocomplete-${Math.random().toString(36).substr(2, 9)}`,
  keyfilter: undefined,
  maxLength: undefined,
  minLength: undefined,
  pattern: undefined,
  variant: 'outlined',
  clearable: false,
  validationRules: () => [],
  validateOnBlur: true,
  validateOnInput: false,
  validateOnChange: true,
  showErrors: true,
  name: undefined,
  formContext: true,
  validationType: undefined,
  customRules: undefined,
  dropdown: false,
  dropdownMode: 'blank',
  autoHighlight: false,
  multiple: false,
  forceSelection: false,
  minInputLength: 1,
  delay: 300,
  completeOnFocus: false,
  scrollHeight: '200px',
  virtualScrollerOptions: undefined,
  showClear: false,
  loading: false,
  loadingIcon: 'pi pi-spinner pi-spin',
  removeTokenIcon: 'pi pi-times-circle',
  dropdownIcon: 'pi pi-chevron-down',
  selectOnFocus: false,
  autoSelection: true,
  panelStyle: undefined,
  panelClass: undefined,
  dataKey: undefined,
  showEmptyMessage: false,
  emptyMessage: '',
  showNoResults: true,
  noResultsMessage: 'No results found',
  selectionMode: 'single',
  itemSize: undefined
})

// Function to safely get option label
const getOptionLabel = (option: any): string => {
  if (typeof option === 'string') return option
  if (props.optionLabel && typeof props.optionLabel === 'string' && option[props.optionLabel] !== undefined) {
    return option[props.optionLabel]
  }
  if (props.field && typeof props.field === 'string' && option[props.field] !== undefined) {
    return option[props.field]
  }
  return option.toString()
}

// Local State
const autoCompleteRef = ref<any>(null)
const isFocused = ref(false)
const currentSearchValue = ref('')

// Setup VeeValidate
const {
  fieldValue,
  errorMessage,
  shouldShowError,
  validate,
  handleValidation,
  updateValue,
  isEmpty,
  touched
} = useVeeValidate({
  name: props.name || props.id,
  modelValue: props.modelValue as string | number | boolean | any[] | null | undefined,
  required: props.required,
  validationType: props.validationType,
  minLength: props.minLength,
  maxLength: props.maxLength,
  customRules: props.customRules
})

// Function to highlight matched text
const highlightText = (text: string): string => {
  if (!currentSearchValue.value) return text

  const normalizedText = text.toString()
  const searchRegex = new RegExp(currentSearchValue.value, 'gi')
  return normalizedText.replace(searchRegex, match => `<span class="text-primary-500 font-medium">${match}</span>`)
}

const onComplete = (event: AutoCompleteCompleteEvent) => {
  currentSearchValue.value = event.query
  emit('complete', event)
}

const emit = defineEmits<{
  'update:modelValue': [value: any]
  complete: [event: AutoCompleteCompleteEvent]
  'item-select': [event: any]
  'item-unselect': [event: any]
  'dropdown-click': [event: any]
  input: [event: Event]
  focus: [event: Event]
  blur: [event: Event]
  keydown: [event: Event]
  change: [event: AutoCompleteChangeEvent]
  clear: []
  show: []
  hide: []
  'validation-error': [errors: string[]]
  'validation-success': []
}>()

// Form Context
const { formState } = useFormContext()
const { t } = useLocalization()

// Local model for v-model binding
const localValue = computed({
  get: () => fieldValue.value,
  set: (newValue) => {
    updateValue(newValue)
    emit('update:modelValue', newValue)
    if (formState && props.name) {
      formState.setFieldValue(props.name, newValue)
    }
  }
})

// Watch for external modelValue changes
watch(() => props.modelValue, (newVal) => {
  updateValue(newVal)
}, { immediate: true })

const isFilled = computed(() => {
  if (props.multiple) {
    return Array.isArray(props.modelValue) && props.modelValue.length > 0
  }
  return props.modelValue != null && props.modelValue.toString().length > 0
})

// Format error message based on validation type
const getFormattedErrorMessage = computed(() => {
  if (!errorMessage.value) return ''

  if (props.required && isEmpty(localValue.value)) {
    return props.name ? `${props.name} is required` : 'This field is required'
  }

  switch (props.validationType) {
    case 'email':
      return 'Please enter a valid email address'
    case 'phone':
      return 'Please enter a valid phone number'
    case 'url':
      return 'Please enter a valid URL'
    case 'numeric':
      return 'Please enter numbers only'
    case 'alpha':
      return 'Please enter letters only'
    case 'alphanumeric':
      return 'Please enter letters and numbers only'
    default:
      return errorMessage.value
  }
})

const isInvalid = computed(() => {
  return props.invalid || shouldShowError.value
})

const helperTextId = computed(() => `${props.id}-helper`)

const ariaDescribedBy = computed(() => {
  const ids = []
  if (props.helperText) ids.push(helperTextId.value)
  if (shouldShowError.value) ids.push(`${props.id}-error`)
  return ids.join(' ')
})

const showErrorMessages = computed(() => {
  return props.showErrors && (!!props.helperText || shouldShowError.value)
})

const onInput = async (event: Event) => {
  const target = event.target as HTMLInputElement
  currentSearchValue.value = target.value || ''
  emit('input', event)

  updateValue(target.value)

  if (props.validateOnInput && shouldShowError.value) {
    await validate()
  }
}

// Watch for value changes to update search value
watch(() => localValue.value, (newValue) => {
  if (!newValue) {
    currentSearchValue.value = ''
  }
})

const onFocus = (event: Event) => {
  isFocused.value = true
  emit('focus', event)
}

const onBlur = async (event: Event) => {
  isFocused.value = false
  emit('blur', event)

  if (formState && props.name) {
    formState.setFieldTouched(props.name, true)
  }

  // Handle validation on blur
  await handleValidation()
}

const onKeydown = (event: Event) => {
  if (props.keyfilter) {
    const keyEvent = event as KeyboardEvent
    const char = keyEvent.key

    if (char.length === 1 && !keyEvent.ctrlKey && !keyEvent.altKey) {
      let regex: RegExp

      if (typeof props.keyfilter === 'string') {
        switch (props.keyfilter) {
          case 'int':
            regex = /[\d\-]/
            break
          case 'number':
            regex = /[\d\-\.]/
            break
          case 'alpha':
            regex = /[a-zA-Z]/
            break
          case 'alphanum':
            regex = /[a-zA-Z0-9]/
            break
          default:
            regex = new RegExp(props.keyfilter)
        }
      } else {
        regex = props.keyfilter
      }

      if (!regex.test(char)) {
        event.preventDefault()
      }
    }
  }
  emit('keydown', event)
}

const onChange = async (event: AutoCompleteChangeEvent) => {
  emit('change', event)

  if (props.validateOnChange) {
    await validate()
  }
}

const onItemSelect = async (event: any) => {
  emit('item-select', event)

  if (props.validateOnChange) {
    await validate()
  }
}

const onItemUnselect = async (event: any) => {
  emit('item-unselect', event)

  if (props.validateOnChange) {
    await validate()
  }
}

const onClear = () => {
  const emptyValue: any = props.multiple ? [] : null
  currentSearchValue.value = ''
  
  // Clear the local value first
  localValue.value = emptyValue
  
  // Emit the clear event
  emit('clear')
  
  // Update the value
  updateValue(emptyValue)
  
  // Emit the model value update
  emit('update:modelValue', emptyValue)

  if (formState && props.name) {
    formState.setFieldValue(props.name, emptyValue)
  }
  
  // Clear the autocomplete input field directly
  if (autoCompleteRef.value) {
    const inputElement = autoCompleteRef.value.$el.querySelector('input')
    if (inputElement) {
      inputElement.value = ''
    }
  }
}

const focus = () => {
  autoCompleteRef.value?.$el.querySelector('input')?.focus()
}

// Initialize form context
onMounted(() => {
  if (formState && props.name && props.formContext) {
    // If using form context, we can still register custom validation rules
    if (props.validationRules && props.validationRules.length > 0) {
      formState.addValidation(props.name, props.validationRules)
    }
  }
})

defineExpose({
  focus,
  blur: () => autoCompleteRef.value?.$el.querySelector('input')?.blur(),
  clear: onClear,
  validate,
  errorMessage,
  isValid: computed(() => !shouldShowError.value)
})
</script>

<style scoped>
/* Styling for validation failure */
:deep(.p-autocomplete.p-invalid .p-autocomplete-input) {
  border-color: var(--red-500, #f44336) !important;
}

:deep(.p-autocomplete.p-invalid:hover .p-autocomplete-input) {
  border-color: var(--red-600, #e53935) !important;
}

:deep(.p-autocomplete.p-invalid.p-focus .p-autocomplete-input) {
  border-color: var(--red-500, #f44336) !important;
  box-shadow: 0 0 0 1px rgba(244, 67, 54, 0.2) !important;
}

/* Change placeholder color to red for invalid state */
:deep(.p-autocomplete.p-invalid .p-autocomplete-input::placeholder) {
  color: #ef4444 !important;
  /* Red-500 from Tailwind */
}

/* Error message styling */
.p-error-messages {
  margin-top: 0.25rem;
}
</style>
